import React, { useEffect, useState } from "react";
import {
  Loader2,
  CreditCard,
  Calendar,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  XCircle,
  RotateCcw,
  Crown,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  fetchMySubscription,
  fetchMyPayments,
  cancelMySubscription,
  reactivateMySubscription,
  openCustomerPortal,
} from "../../lib/studioApi";

const TIER_LABELS = {
  studio_membership: { label: "Studio Membership", Icon: Sparkles },
  studio_plus: { label: "Studio Plus", Icon: Crown },
  free: { label: "No active plan", Icon: Sparkles },
};

const STATUS_COLOR = {
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  trialing: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  past_due: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  canceled: "bg-red-500/15 text-red-300 border-red-500/30",
  unpaid: "bg-red-500/15 text-red-300 border-red-500/30",
  incomplete: "bg-white/10 text-white/60 border-white/15",
};

function unixToDate(ts) {
  if (!ts || ts === 0) return "—";
  try {
    return new Date(ts * 1000).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function unixToFullDate(ts) {
  if (!ts || ts === 0) return "—";
  try {
    return new Date(ts * 1000).toLocaleString();
  } catch {
    return "—";
  }
}

export default function SubscriptionPanel() {
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'cancel' | 'reactivate' | 'portal'

  const load = () => {
    setLoading(true);
    Promise.all([fetchMySubscription(), fetchMyPayments().catch(() => [])])
      .then(([sub, pays]) => {
        setData(sub);
        setPayments(pays || []);
      })
      .catch(() => {
        toast.error("Could not load subscription");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <section className="bg-[#0f1322] border border-white/5 rounded-2xl p-6 md:p-7">
        <Loader2 className="w-6 h-6 animate-spin text-paul-yellow mx-auto" />
      </section>
    );
  }

  if (!data) return null;

  const tier = data.tier || "free";
  const { label, Icon } = TIER_LABELS[tier] || TIER_LABELS.free;
  const sub = data.subscription;
  const hasSub = data.hasSubscription && sub;
  const isAdminCreated = data.accountSource === "admin";

  // Admin-created users (grandfathered) have no Stripe subscription.
  if (!hasSub) {
    return (
      <section
        data-testid="subscription-panel"
        className="bg-[#0f1322] border border-white/5 rounded-2xl p-6 md:p-7"
      >
        <h2 className="text-lg font-extrabold text-white mb-1 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-paul-yellow" />
          Subscription
        </h2>
        <p className="text-sm text-white/55 mb-5">
          {isAdminCreated
            ? "You're on a Paul-invited access plan — no subscription required. Welcome to the studio."
            : "You don't have an active subscription yet."}
        </p>
        {!isAdminCreated && (
          <a
            href="/studio/login"
            className="inline-flex items-center gap-2 bg-paul-yellow text-navy font-bold text-sm px-4 py-2.5 rounded-lg hover:brightness-105"
            style={{ color: "#0b1535" }}
          >
            <Sparkles className="w-4 h-4" />
            See pricing
          </a>
        )}
      </section>
    );
  }

  const status = sub.status || "active";
  const statusClass = STATUS_COLOR[status] || STATUS_COLOR.incomplete;
  const autoRenew = !sub.cancelAtPeriodEnd;
  const billingType = sub.billingType || "monthly";
  const renewDate = unixToDate(sub.currentPeriodEnd);

  const handleCancel = async () => {
    if (!window.confirm(
      "Cancel auto-renewal? You'll keep access until the end of the current billing period.",
    )) return;
    setBusy("cancel");
    try {
      await cancelMySubscription();
      toast.success("Auto-renewal turned off. Access continues until period ends.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel");
    } finally {
      setBusy(null);
    }
  };

  const handleReactivate = async () => {
    setBusy("reactivate");
    try {
      await reactivateMySubscription();
      toast.success("Subscription reactivated — auto-renew is back on.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reactivate");
    } finally {
      setBusy(null);
    }
  };

  const handlePortal = async () => {
    setBusy("portal");
    try {
      const url = await openCustomerPortal();
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open customer portal");
      setBusy(null);
    }
  };

  return (
    <section
      data-testid="subscription-panel"
      className="bg-[#0f1322] border border-white/5 rounded-2xl p-6 md:p-7"
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-paul-yellow/15 text-paul-yellow flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-white">{label}</h2>
            <div className="text-xs text-white/55 capitalize">
              {billingType} billing
            </div>
          </div>
        </div>
        <span
          data-testid="subscription-status"
          className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${statusClass}`}
        >
          {status === "active" || status === "trialing" ? (
            <CheckCircle2 className="w-3 h-3" />
          ) : (
            <AlertCircle className="w-3 h-3" />
          )}
          {status.replace("_", " ")}
        </span>
      </div>

      {/* Meta grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-5">
        <div className="bg-[#10162a] border border-white/5 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/40 mb-1">
            {sub.cancelAtPeriodEnd ? "Access until" : "Renews on"}
          </div>
          <div className="text-white font-semibold flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-paul-yellow" />
            {renewDate}
          </div>
        </div>
        <div className="bg-[#10162a] border border-white/5 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/40 mb-1">
            Auto-renew
          </div>
          <div className={`font-semibold ${autoRenew ? "text-emerald-300" : "text-amber-300"}`}>
            {autoRenew ? "On" : "Off — ends after current period"}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={handlePortal}
          disabled={busy === "portal"}
          data-testid="sub-update-payment-btn"
          className="bg-paul-yellow hover:brightness-105 text-navy font-bold gap-2"
          style={{ color: "#0b1535" }}
        >
          {busy === "portal" ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ExternalLink className="w-4 h-4" />
          )}
          Update payment method
        </Button>
        {sub.cancelAtPeriodEnd ? (
          <Button
            type="button"
            onClick={handleReactivate}
            disabled={busy === "reactivate"}
            data-testid="sub-reactivate-btn"
            variant="outline"
            className="border-emerald-500/40 bg-transparent text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 gap-2"
          >
            {busy === "reactivate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Reactivate
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleCancel}
            disabled={busy === "cancel"}
            data-testid="sub-cancel-btn"
            variant="outline"
            className="border-white/15 bg-transparent text-white/70 hover:bg-white/10 hover:text-white gap-2"
          >
            {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            Cancel subscription
          </Button>
        )}
      </div>

      {/* Payment history */}
      {payments.length > 0 && (
        <div className="mt-6 pt-5 border-t border-white/5">
          <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-white/50 mb-3">
            Recent payments
          </h3>
          <ul className="space-y-2" data-testid="sub-payments-list">
            {payments.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-3 bg-[#10162a] border border-white/5 rounded-lg px-3 py-2.5"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <CreditCard className="w-4 h-4 text-paul-yellow flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">
                      ${Number(p.amountPaid || 0).toFixed(2)}{" "}
                      <span className="text-[10px] uppercase text-white/40">
                        {String(p.currency || "USD").toUpperCase()}
                      </span>
                    </div>
                    <div className="text-[11px] text-white/40">{unixToFullDate(p.created)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                      p.status === "paid"
                        ? "bg-emerald-500/15 text-emerald-300"
                        : "bg-white/10 text-white/60"
                    }`}
                  >
                    {p.status}
                  </span>
                  {p.invoicePdf && (
                    <a
                      href={p.invoicePdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-paul-yellow hover:text-paul-yellow/80"
                      title="Download invoice PDF"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
